import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import approx from 'approximate-number';
import makeStyles from '@mui/styles/makeStyles';
import Tooltip from 'ripple/Components/Tooltip';
import Badge from '@mui/material/Badge';
import Fab from '@mui/material/Fab';
import Favorite from '@mui/icons-material/Favorite';
import stopEvent from 'lib/stopEvent';
import { useAuthContext } from 'AuthContext';
import { useMutation } from '@apollo/client';
import { colors } from '@mui/material';
import { LIKED_UNLIKED_SPONSOR } from '../../../schema/apolloMutations/eventDeatilsMutations';

const useStyles = makeStyles(theme => {
  return {
    fabIcon: {
      margin: '5px 15px 5px 0px',
      backgroundColor: 'transparent!important',
      border: `3px solid ${theme.palette.text.primary}!important`,
      color: `${theme.palette.text.primary}!important`,
      fontSize: 25,
    },
    liked: {
      color: theme?.palette?.primary?.main,
      fontSize: 25,
    },
    unliked: {
      color: theme?.palette?.text?.primary,
      fontSize: 25,
    },
  };
});

const EventLike = ({ event, viewer }) => {
  const [likeStatus, setLikeStatus] = useState({ liked: false, likeCount: 0 });
  const [liking, setLiking] = useState(false);

  useEffect(() => {
    setLikeStatus({ liked: event?.liked, likeCount: event?.likeCount });
  }, [event?.likeCount, event?.liked]);

  const [handleLike] = useMutation(LIKED_UNLIKED_SPONSOR);

  const styles = useStyles();
  const { handleIntercepted, interceptAction } = useAuthContext();

  const like = () => {
    setLiking(true);
    handleLike({
      variables: {
        isLiked: !likeStatus?.liked,
        id: event?.id,
        model: 'event',
      },
    }).then(likeRes => {
      if (likeRes?.data?.likeOrDislikeSponsor === true) {
        setLikeStatus({ liked: true, likeCount: likeStatus.likeCount + 1 });
      } else {
        setLikeStatus({ liked: false, likeCount: likeStatus.likeCount - 1 });
      }
      setLiking(false);
    });
  };

  const onLike = useCallback(
    e => {
      stopEvent(e);
      if (viewer?.loggedIn) {
        like();
      } else {
        interceptAction(`like-${event?.id}`);
      }
    },
    [event?.id, interceptAction, like, viewer?.loggedIn],
  );

  useEffect(() => {
    if (viewer?.loggedIn) {
      handleIntercepted(`like-${event?.id}`, like);
    }
  }, [event?.id, handleIntercepted, like, viewer?.loggedIn]);

  return (
    <Tooltip
      title={likeStatus?.liked ? 'Unlike' : 'Like'}
      arrow
      placement='top'
      size='medium'
    >
      <Fab
        style={{ zIndex: 0 }}
        disabled={liking}
        size='medium'
        className={styles.fabIcon}
        onClick={onLike}
      >
        <Badge
          badgeContent={approx(likeStatus?.likeCount)}
          color='error'
          max={1000}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          <Favorite
            style={likeStatus?.liked ? { color: colors.red[500] } : {}}
            className={likeStatus?.liked ? styles.liked : styles.unliked}
          />
        </Badge>
      </Fab>
    </Tooltip>
  );
};

EventLike.propTypes = {
  event: PropTypes.object.isRequired,
  viewer: PropTypes.object.isRequired,
  liked: PropTypes.bool.isRequired,
  likeCount: PropTypes.number.isRequired,
};

export default EventLike;
